import React from 'react'
import { useGlobal } from 'reactn'
import {
  ATLButton,
  ATLTextBody,
  ATLTextHead,
  ButtonTypeEnum,
  TextAlignmentEnum,
} from '@appsimples/atlantica-react-native'
import { VisitEventStatusType, VisitorStatusType } from '../../constants/types'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'
import { atlTheme } from '../../atlTheme'
import { formatEventDateTime, formatDateWithMonthName } from '../../utils/formatters'
import { TextTwoColors } from '../../components/TextTwoColors'
import { RaffleInfoContainer } from './styles'
import { ButtonWithLoading } from '../../components/button-with-loading/ButtonWithLoading'

interface RafflesListProps {
  currentRaffleId: string
  joinRaffleCallback: (id: string) => void
  quitRaffleCallback: (id: string) => void
  isJoining: boolean
  isQuitting: boolean
}

export const RafflesList: React.FC<RafflesListProps> = ({
  currentRaffleId,
  joinRaffleCallback,
  quitRaffleCallback,
  isJoining,
  isQuitting,
}) => {
  const [availableRaffles] = useGlobal('availableVisitationEvents')
  const [userSaleContracts] = useGlobal('userSaleContracts')

  const getConstructionName = (constructionId: string) => {
    return userSaleContracts?.find(item => item.construction.id === constructionId)?.construction?.name
  }

  return (
    <>
      {availableRaffles
        ?.filter(item => item.visitEvent.eventStatus === VisitEventStatusType.open)
        .map(item => {
          const visitationDate = new Date(item.visitEvent.date)
          const drawDate = new Date(item.visitEvent.drawDate)
          const isUserEnrolled = item.subscription?.status === VisitorStatusType.preDraw

          let buttonTitle
          let primaryAction

          if (isUserEnrolled) {
            buttonTitle = formatMessage(messages.appointments_leaveTheRaffle)
            primaryAction = () => quitRaffleCallback(item.visitEvent.id)
          } else {
            buttonTitle = formatMessage(messages.appointments_joinTheRaffle)
            primaryAction = () => joinRaffleCallback(item.visitEvent.id)
          }

          const shouldShowLoadingIndicator = (isJoining || isQuitting) && currentRaffleId === item.visitEvent.id

          const constructionName = getConstructionName(item.visitEvent.construction.id)

          return (
            <>
              <ATLTextHead
                textAlignment={TextAlignmentEnum.left}
                labelBaseProps={{ fontWeight: atlTheme.FontWeight500 }}
              >
                {formatMessage(messages.appointments_raffleToVisitConstructionLabel)}
              </ATLTextHead>
              <RaffleInfoContainer>
                <TextTwoColors
                  firstText={formatMessage(messages.appointments_raffleToVisitConstructionEnterprise)}
                  secondText={constructionName}
                />
                <TextTwoColors
                  firstText={formatMessage(messages.appointments_raffleToVisitConstruction_visitationDate)}
                  secondText={formatEventDateTime(
                    formatDateWithMonthName(visitationDate),
                    visitationDate.getHours().toString()
                  )}
                />
                <TextTwoColors
                  firstText={formatMessage(messages.appointments_raffleToVisitConstruction_raffleDate)}
                  secondText={formatEventDateTime(formatDateWithMonthName(drawDate), drawDate.getHours().toString())}
                />
              </RaffleInfoContainer>
              {isUserEnrolled && (
                <ATLTextBody
                  textAlignment={TextAlignmentEnum.center}
                  labelBaseProps={{ textColor: atlTheme.ColorMediumContrast, multilineLimit: 2 }}
                >
                  {formatMessage(messages.appointments_youAlreadyEnrolledForTheRaffle)}
                </ATLTextBody>
              )}
              {shouldShowLoadingIndicator ? (
                <ButtonWithLoading />
              ) : (
                <ATLButton
                  title={buttonTitle}
                  type={ButtonTypeEnum.outlined}
                  onPress={primaryAction}
                  buttonBaseProps={{ borderRadius: atlTheme.BorderRadius100 }}
                />
              )}
            </>
          )
        })}
    </>
  )
}
